import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { DEFAULT_TEXTS } from "../i18n/translations";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_timeshare-calculator/artifacts/7gkgelre_LOGOTIPO%20ALTERNATIVO%20blanco.png";

const Tab = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`border-b-2 px-1 py-3 text-xs uppercase tracking-luxury transition-colors ${
      active ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/40 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const SettingsTab = () => {
  const { settings, reloadSettings, t } = useApp();
  const [inflation, setInflation] = useState(settings.inflation_rate);
  const [endYear, setEndYear] = useState(settings.end_year);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInflation(settings.inflation_rate);
    setEndYear(settings.end_year);
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        inflation_rate: Number(inflation),
        end_year: Number(endYear),
      });
      await reloadSettings();
      toast.success(t("admin_saved"));
    } catch (e) {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-tab">
      <div>
        <label className="text-xs uppercase tracking-luxury text-white/50">
          {t("admin_inflation")}
        </label>
        <input
          type="number"
          step="0.001"
          value={inflation}
          onChange={(e) => setInflation(e.target.value)}
          className="mt-2 w-full border-0 border-b border-white/15 bg-transparent px-0 py-3 font-display text-2xl text-white outline-none focus:border-[#D4AF37]"
          data-testid="settings-inflation-input"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-luxury text-white/50">
          {t("admin_end_year")}
        </label>
        <input
          type="number"
          value={endYear}
          onChange={(e) => setEndYear(e.target.value)}
          className="mt-2 w-full border-0 border-b border-white/15 bg-transparent px-0 py-3 font-display text-2xl text-white outline-none focus:border-[#D4AF37]"
          data-testid="settings-year-input"
        />
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-[#D4AF37] px-8 py-3 text-xs font-bold uppercase tracking-luxury text-black transition-colors hover:bg-white"
        data-testid="settings-save-btn"
      >
        <Save size={14} /> {saving ? "..." : t("admin_save")}
      </button>
    </div>
  );
};

const TextsTab = () => {
  const { settings, reloadSettings, t } = useApp();
  const [lang, setLang] = useState("es");
  const [texts, setTexts] = useState(() => ({
    es: { ...DEFAULT_TEXTS.es, ...(settings.texts?.es || {}) },
    en: { ...DEFAULT_TEXTS.en, ...(settings.texts?.en || {}) },
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTexts({
      es: { ...DEFAULT_TEXTS.es, ...(settings.texts?.es || {}) },
      en: { ...DEFAULT_TEXTS.en, ...(settings.texts?.en || {}) },
    });
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { texts });
      await reloadSettings();
      toast.success(t("admin_saved"));
    } catch (e) {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  const editableKeys = [
    "hero_eyebrow",
    "hero_title",
    "hero_subtitle",
    "cta_start",
    "q_membership",
    "opt_no_membership",
    "opt_with_membership",
    "q_years",
    "q_nights",
    "q_price",
    "q_fee",
    "benefits_title",
    "benefits_subtitle",
    "persuasive_title",
    "persuasive_desc",
    "contact_title",
    "contact_subtitle",
    "contact_cta",
  ];

  return (
    <div className="space-y-6" data-testid="texts-tab">
      <div className="flex gap-3">
        {["es", "en"].map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLang(l)}
            className={`px-4 py-2 text-xs uppercase tracking-luxury transition-colors ${
              lang === l ? "bg-[#D4AF37] text-black" : "bg-white/5 text-white/50"
            }`}
            data-testid={`texts-lang-${l}`}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6">
        {editableKeys.map((k) => (
          <div key={k}>
            <label className="text-xs uppercase tracking-luxury text-white/50">
              {k}
            </label>
            <textarea
              value={texts[lang][k] || ""}
              onChange={(e) =>
                setTexts({
                  ...texts,
                  [lang]: { ...texts[lang], [k]: e.target.value },
                })
              }
              rows={k.includes("title") || k.includes("desc") || k.includes("subtitle") ? 2 : 1}
              className="mt-2 w-full border border-white/10 bg-[#0a0a0c] px-4 py-3 font-body text-sm text-white outline-none focus:border-[#D4AF37]"
              data-testid={`text-input-${k}`}
            />
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-[#D4AF37] px-8 py-3 text-xs font-bold uppercase tracking-luxury text-black transition-colors hover:bg-white"
        data-testid="texts-save-btn"
      >
        <Save size={14} /> {saving ? "..." : t("admin_save")}
      </button>
    </div>
  );
};

const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      toast.error("Error loading leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads(leads.filter((l) => l.id !== id));
    } catch (e) {
      toast.error("Error");
    }
  };

  if (loading) return <p className="text-sm text-white/50">...</p>;
  if (leads.length === 0)
    return <p className="text-sm text-white/50" data-testid="leads-empty">No leads yet.</p>;

  return (
    <div className="overflow-x-auto" data-testid="leads-tab">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 text-xs uppercase tracking-luxury text-white/50">
            <th className="py-3">Date</th>
            <th>Name</th>
            <th>Phone</th>
            <th>Scenario</th>
            <th>Projected</th>
            <th>Lang</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-white/5" data-testid={`lead-row-${l.id}`}>
              <td className="py-4 text-xs text-white/50">
                {new Date(l.created_at).toLocaleString()}
              </td>
              <td className="font-display text-base text-white">{l.name}</td>
              <td className="text-white/80">{l.phone}</td>
              <td className="text-xs uppercase tracking-wider text-white/50">
                {l.scenario}
              </td>
              <td className="font-display text-base text-[#D4AF37]">
                ${Math.round(l.projected_total).toLocaleString()}
              </td>
              <td className="text-xs uppercase text-white/60">{l.language}</td>
              <td>
                <button
                  type="button"
                  onClick={() => del(l.id)}
                  className="text-white/40 hover:text-red-400"
                  data-testid={`lead-delete-${l.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout, checking } = useAuth();
  const { t } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    if (!checking && !user) navigate("/admin/login");
  }, [user, checking, navigate]);

  if (checking || !user) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-dashboard">
      <header className="border-b border-[#E5E0D8] bg-[#0B1D3A] text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 sm:px-8">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Ourigen" className="h-9 w-9 object-contain" />
            <span className="font-display tracking-[0.4em]">OURIGEN</span>
            <span className="ml-2 text-xs uppercase tracking-luxury text-white/50">
              {t("admin_title")}
            </span>
          </div>
          <button
            type="button"
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 text-xs uppercase tracking-luxury text-white/70 hover:text-[#D4AF37]"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> {t("admin_logout")}
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-12 sm:px-8">
        <nav className="mb-10 flex gap-8 border-b border-white/10">
          <Tab active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings">
            {t("admin_settings")}
          </Tab>
          <Tab active={tab === "texts"} onClick={() => setTab("texts")} testid="tab-texts">
            {t("admin_texts")}
          </Tab>
          <Tab active={tab === "leads"} onClick={() => setTab("leads")} testid="tab-leads">
            {t("admin_leads")}
          </Tab>
        </nav>
        <div className="bg-[#131316] border border-white/10 p-8 sm:p-12">
          {tab === "settings" && <SettingsTab />}
          {tab === "texts" && <TextsTab />}
          {tab === "leads" && <LeadsTab />}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
